import {forkJoin, Observable, of} from 'rxjs';
import {map, mergeMap, switchMap} from 'rxjs/operators';
import {inject, Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, ResolveFn, RouterStateSnapshot} from '@angular/router';
import {EquipmentResponse} from '../collectors-equipment/equipment.types';
import {BaseService} from '@shared/services/api/base-service';
import {AceLogger} from '@logging/ace-logger';
import {AceLoggerService} from '@logging/ace-logger.service';
import {Collector, CollectorType, Device, Equipment, Level, MetadataType, VirtualFile} from '@app/graphql-typings/graphql';
import {EquipmentSearchCriteria, EquipmentService} from '@shared/services/api/equipment.service';
import {RoutingStateService} from "@shared/services/routing/routing-state.service";
import {CommissionDataNavigationState} from "@app/layout/sitemanager/types/types";
import {SiteInfo} from "@shared/services/api/access-sites.interface";
import {SiteService} from "@shared/services";
import {NavigationCacheService} from "@app/layout/sitemanager/services/navigation-cache.service";
import {StringUtils} from '@shared/util/general/string-utils';


export const collectorEquipmentUpsertResolver: ResolveFn<EquipmentResponse> = (route: ActivatedRouteSnapshot, _state: RouterStateSnapshot): Observable<EquipmentResponse> => {
  const navigationState: CommissionDataNavigationState = inject(RoutingStateService).getNavigationState<CommissionDataNavigationState>();
  const collectorEquipmentUpsertService: CollectorEquipmentUpsertService = inject(CollectorEquipmentUpsertService);
  const siteService: SiteService = inject(SiteService);
  const equipmentService: EquipmentService = inject(EquipmentService);
  const navigationCacheService: NavigationCacheService = inject(NavigationCacheService);
  const equipmentId: number = StringUtils.stringToNumber(route.paramMap.get('id'));
  const siteId: number = StringUtils.stringToNumber(route.paramMap.get('site-id'));
  const collectorType: CollectorType = route.data.collector_type;

  if (siteId == null) {
    throw new Error('Site id must be specified');
  }

  const collector$ = navigationState?.collector
    ? of(navigationState.collector)
    : navigationCacheService.getSiteCollector(siteId, collectorType);
  return collector$.pipe(switchMap((collector: Collector): Observable<EquipmentResponse> => {
    const site$ = siteService.getCachedSiteInfo(siteId);
    // Always get the equipment to update from the API to ensure that the latest data for that equipment is being worked with (rather
    // than potentially stale data in the NavigationState)
    const equipment$ = equipmentId === null
      ? of(null)
      : equipmentService.getEquipmentById(equipmentId, {
        ...navigationCacheService.getEquipmentScopeOptions(collectorType), getParentEquipment: true, getEquipmentProperties: true, getExternalReferences: true});
    const collectorEquipment$ = navigationState?.equipment
      ? of(navigationState?.equipment)
      : navigationCacheService.getEquipment(collector.collector_id, collectorType);
    const levels$ = navigationState?.levels
      ? of(navigationState.levels)
      : navigationCacheService.getSiteLevels(siteId);
    const metadataTypes$ = navigationState?.metadataTypes
      ? of(navigationState.metadataTypes)
      : navigationCacheService.getMetadataTypes();
    const files$ = navigationState?.files
      ? of(navigationState.files)
      : collectorType === CollectorType.Virtual ? navigationCacheService.getFiles(siteId) : of(null);
    const devices$ = navigationState?.devices
      ? of(navigationState.devices)
      : collectorType === CollectorType.Physical ? navigationCacheService.getDevices(collector.collector_id) : of(null);

    return forkJoin([site$, equipment$, collectorEquipment$, levels$, metadataTypes$, files$, devices$])
      .pipe(mergeMap(([site, equipment, collectorEquipment, levels, metadataTypes, files, devices]:
                    [SiteInfo, Equipment, Equipment[], Level[], MetadataType[], VirtualFile[] | null, Device[] | null]) => {
          const relatedEquipment$: Observable<Equipment[]> = collectorEquipmentUpsertService.getRelatedEquipment(collector.collector_id, equipment, collectorEquipment);

          return forkJoin([of(site), of(equipment), of(collectorEquipment), of(levels), of(metadataTypes), of(files), of(devices), relatedEquipment$]);
        }),
        map(([site, equipment, collectorEquipment, levels, metadataTypes, files, devices, relatedEquipment]:
               [SiteInfo, Equipment, Equipment[], Level[], MetadataType[], VirtualFile[] | null, Device[] | null, Equipment[]]) => {
          return {site, collector, equipment, collectorEquipment, levels, metadataTypes, files, devices, relatedEquipment};
        }));
  }));
};

@Injectable()
export class CollectorEquipmentUpsertService extends BaseService {
  private readonly logger: AceLogger;

  constructor(private equipmentService: EquipmentService,
              logService: AceLoggerService) {
    super();
    this.logger = logService.create(CollectorEquipmentUpsertService.name);
  }

  getRelatedEquipment(collectorId: number, equipment: Equipment, allEquipment: Equipment[]): Observable<Equipment[]> {
    if (equipment?.related_equipment?.length) {
      if (allEquipment) {
        const relatedEquipmentIds = new Set(equipment.related_equipment);
        const relatedEquipment: Equipment[] = [];
        for (const e of allEquipment) {
          if (relatedEquipmentIds.has(e.equipment_id)) {
            relatedEquipment.push(e);
            relatedEquipmentIds.delete(e.equipment_id);
            if (!relatedEquipmentIds.size) {
              return of(relatedEquipment);
            }
          }
        }
      }

      // If we got here then either there was no allEquipment passed in, or could not find all the related equipment allEquipment
      // Try looking up using the API
      const relatedEquipmentSearchTerm: EquipmentSearchCriteria = {
        collector_id: collectorId,
        equipment_ids: equipment.related_equipment,
        commissionable: true
      };

      return this.equipmentService.searchAllEquipment(relatedEquipmentSearchTerm);
    } else {
      return of([]);
    }
  }


}
